import React, { useState, useEffect } from 'react';
import { 
    MdPeople, 
    MdAssignment, 
    MdTrendingUp, 
    MdStar,
    MdCalendarToday,
    MdMoreVert
} from 'react-icons/md';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import Cookies from 'js-cookie';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import axios from 'axios';
import { FaEye } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import API_BASE_URL from '../../../config/api';

const EmployeeDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const [profile, setProfile] = useState(null);
    const [profileLoading, setProfileLoading] = useState(true);
    const baseUrl = API_BASE_URL

    // Add these state hooks at the top of your component
    const [loadingReviews, setLoadingReviews] = useState(true);
    const [reviewsError, setReviewsError] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [availableYears, setAvailableYears] = useState([]);
    const [filteredReviews, setFilteredReviews] = useState([]);

    // Fetch profile first to get employee_id
    useEffect(() => {
        const fetchProfile = async () => {
            setProfileLoading(true);
            try {
                const token = Cookies.get('token');
                const response = await axios.get(`${baseUrl}/api/users/me`, {
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                    },
                });
                setProfile(response.data);
            } catch (err) {
                setProfile(null);
            } finally {
                setProfileLoading(false);
            }
        };
        fetchProfile();
    }, []);

    // Fetch analytics for the employee once profile is loaded
    useEffect(() => {
        const fetchAnalytics = async () => {
            if (!profile?.employee?.id) return;
            setLoading(true);
            try {
                const token = Cookies.get('token');
                const currentYear = new Date().getFullYear();
                const response = await axios.get(`${baseUrl}/api/performance-analytics`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                    params: { 
                        employee: profile.employee.id,
                        year: currentYear
                    }
                });
                setAnalytics(response.data);
            } catch (err) {
                setAnalytics(null);
            } finally {
                setLoading(false);
            }
        };
        if (profile && !profileLoading) {
            fetchAnalytics();
        }
    }, [profile, profileLoading]);

    useEffect(() => {
        const fetchReviews = async () => {
            setLoadingReviews(true);
            setReviewsError(null);
            try {
                const token = Cookies.get('token');
                const response = await axios.get(`${baseUrl}/api/goals/`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
                // Only completed goals with hod_score > 0
                const completed = (Array.isArray(response.data) ? response.data : [])
                    .filter(g => (g.status === 'complete' || g.status === 'completed') && Number(g.hod_score) > 0);
                // Sort by most recent if possible (assuming a date field, e.g., updated_at or completed_at)
                completed.sort((a, b) => new Date(b.updated_at || b.completed_at || b.created_at) - new Date(a.updated_at || a.completed_at || a.created_at));
                setReviews(completed);
                
                // Extract available years from reviews
                const years = Array.from(new Set(completed.map(g => g.year).filter(y => y !== undefined && y !== null))).sort((a, b) => b - a);
                setAvailableYears(years);
            } catch (err) {
                setReviewsError('Failed to fetch recent reviews.');
            } finally {
                setLoadingReviews(false);
            }
        };
        fetchReviews();
    }, []);

    // Filter reviews by selected year
    useEffect(() => {
        if (reviews.length > 0) {
            const filtered = reviews.filter(review => {
                if (review.year !== undefined && review.year !== null) {
                    return review.year === selectedYear;
                }
                // Fallback to date-based filtering if year field is not available
                const reviewDate = new Date(review.updated_at || review.completed_at || review.created_at);
                return reviewDate.getFullYear() === selectedYear;
            });
            setFilteredReviews(filtered);
        }
    }, [reviews, selectedYear]);

    const stats = analytics?.overall_statistics || {};
    const timeliness = analytics?.overall_statistics || {};


    const COLORS = ['#4F46E5', '#818CF8', '#A5B4FC', '#C7D2FE'];

    const StatCard = ({ icon: Icon, title, value, trend, loading, children }) => (
        <div className="stat-card">
            {loading ? (
                <Skeleton height={100} />
            ) : (
                <>
                    <div className="stat-icon">
                        <Icon />
                    </div>
                    <div className="stat-content">
                        <h3>{title}</h3>
                        <div className="stat-value">{value}</div>
                        {children}
                        {trend && <div className="stat-trend">{trend}</div>}
                    </div>
                </>
            )}
        </div>
    );

    const ChartCard = ({ title, children, loading }) => (
        <div className="chart-card">
            {loading ? (
                <Skeleton height={300} />
            ) : (
                <>
                    <div className="chart-header">
                        <h3>{title}</h3>
                        <button className="more-btn">
                            <MdMoreVert />
                        </button>
                    </div>
                    <div className="chart-content">
                        {children}
                    </div>
                </>
            )}
        </div>
    );

    // Transform quarterly_trends object to array for recharts
    const quarterlyTrendsData = React.useMemo(() => {
        if (!analytics?.quarterly_trends) return [];
        return Object.entries(analytics.quarterly_trends).map(([quarter, stats]) => ({
            quarter,
            ...stats
        }));
    }, [analytics]);

    // RecentEvaluations component
    const RecentEvaluations = ({ reviews, availableYears, selectedYear, onYearChange }) => {
        const handleViewAllReviews = () => {
            // Placeholder: navigate or open all reviews page/modal
            // You can use navigate('/employee/reviews') if using react-router
        };
        return (
            <div className="recent-evaluations-card" style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: 24, marginTop: 32 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: '12px' }}>
                    <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Recent Reviews</h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        
                        <Link
                            to="/employee/goal-reviews"
                            style={{
                                background: '#4F46E5',
                                color: 'white',
                                border: 'none',
                                borderRadius: 8,
                                padding: '6px 18px',
                                fontWeight: 600,
                                fontSize: 14,
                                cursor: 'pointer',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                transition: 'background 0.2s',
                                textDecoration: 'none',
                                display: 'inline-block',
                            }}
                        >
                            View All
                        </Link>
                    </div>
                </div>
                {loadingReviews ? (
                    <div style={{ color: '#888', textAlign: 'center', padding: 32 }}>Loading...</div>
                ) : reviewsError ? (
                    <div style={{ color: '#ef4444', textAlign: 'center', padding: 32 }}>{reviewsError}</div>
                ) : (!reviews || reviews.length === 0) ? (
                    <div style={{ color: '#888', textAlign: 'center', padding: 32 }}>No recent reviews found for {selectedYear}.</div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f3f4f6' }}>
                                    <th style={{ textAlign: 'left', padding: '10px 8px', fontWeight: 600, fontSize: 15 }}>Activity / KPI</th>
                                    <th style={{ textAlign: 'left', padding: '10px 8px', fontWeight: 600, fontSize: 15 }}>Performance Standard / Target</th>
                                    <th style={{ textAlign: 'left', padding: '10px 8px', fontWeight: 600, fontSize: 15 }}>Metric</th>
                                    <th style={{ textAlign: 'left', padding: '10px 8px', fontWeight: 600, fontSize: 15 }}>Self Score</th>
                                    <th style={{ textAlign: 'left', padding: '10px 8px', fontWeight: 600, fontSize: 15 }}>HOD Score</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reviews.slice(0, 5).map((review, idx) => (
                                    <tr key={review.id || idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <td style={{ padding: '10px 8px', maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{review.activity || '-'}</td>
                                        <td style={{ padding: '10px 8px', maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{review.performance_standard || '-'}</td>
                                        <td style={{ padding: '10px 8px', maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{review.metric || '-'}</td>
                                        <td style={{ padding: '10px 8px', color: '#4F46E5', fontWeight: 600 }}>{review.score != null ? review.score : '-'}</td>
                                        <td style={{ padding: '10px 8px', maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{review.hod_score != null ? review.hod_score : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
        <Topbar />
        <Sidebar />
        <div className="dashboard-container">
            <div className="dashboard-header">
                <h1>Performance Dashboard</h1>
                <p>Overview of employee performance metrics and KPIs</p>
            </div>

            <div className="stats-grid">
                <StatCard
                    icon={MdStar}
                    title="Completed KPIs"
                    value={`${stats.completed_goals} / ${stats.total_goals}`}
                    loading={loading}
                />
                
                <StatCard
                    icon={MdTrendingUp}
                    title="Avg Performance"
                    value={stats.average_progress ? `${stats.average_progress}%` : '0%'}
                    loading={loading}
                />
                
                <StatCard
                    icon={MdStar}
                    title="KPIs Completion Timeliness"
                    value={null}
                    loading={loading}
                >
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap', marginTop: 4 }}>
                        <span style={{ background: '#22c55e', color: 'white', borderRadius: 8, padding: '2px 8px', fontWeight: 600, fontSize: 12, lineHeight: '18px' }}>
                            On Time: {timeliness.on_time_rate != null ? timeliness.on_time_rate : 0}%
                        </span>
                        <span style={{ background: '#3b82f6', color: 'white', borderRadius: 8, padding: '2px 8px', fontWeight: 600, fontSize: 12, lineHeight: '18px' }}>
                            Early: {timeliness.early_rate != null ? timeliness.early_rate : 0}%
                        </span>
                        <span style={{ background: '#ef4444', color: 'white', borderRadius: 8, padding: '2px 8px', fontWeight: 600, fontSize: 12, lineHeight: '18px' }}>
                            Late: {timeliness.late_rate != null ? timeliness.late_rate  : 0}%
                        </span>
                    </div>
                </StatCard>
               
                
            </div>

            <div className="charts-grid">

                <ChartCard title="Quarterly Performance Trends" loading={loading}>
                    <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={quarterlyTrendsData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorQuarterly" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.8}/>
                                    <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="quarter" />
                            <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                            <Tooltip formatter={(value) => `${value}%`} />
                            <Area 
                                type="monotone" 
                                dataKey="completion_rate" 
                                stroke="#4F46E5" 
                                fillOpacity={1} 
                                fill="url(#colorQuarterly)" 
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>

            <RecentEvaluations 
                reviews={filteredReviews} 
                availableYears={availableYears}
                selectedYear={selectedYear}
                onYearChange={setSelectedYear}
            />
        </div>
        </>
    );
};

export default EmployeeDashboard;
